import { Component, Input, OnDestroy, computed, signal } from "@angular/core"
import { EmployeeMbrad } from "src/app/models/employee/employeeMbrad.model"
import { EmployeeService } from "src/app/services"
import { SuperiorMap, CreateSuperiorMap, SearchableSelectOption } from "src/app/models"
import { of, Subscription, switchMap, tap } from "rxjs"
import { ToastService } from "src/app/services"
import { SearchableSelectComponent } from "../searchable-select/searchable-select.component"
@Component({
    selector: "valben-superior-picker",
    standalone: true,
    imports: [SearchableSelectComponent],
    templateUrl: "./superior-picker.component.html",
})
export class SuperiorPickerComponent implements OnDestroy {
    private _employeeId: string = ""
    private _superiorMaps: SuperiorMap[] = []

    @Input() set employeeId(value: string) {
        const employeeChanged = value !== this._employeeId
        this._employeeId = value
        if (employeeChanged) this.selectedSuperiorId.set(null)
        this.loadSuperiorForEmployee()
    }

    get employeeId(): string {
        return this._employeeId
    }

    @Input() set superiorMaps(value: SuperiorMap[]) {
        this._superiorMaps = value ?? []
        this.loadSuperiorForEmployee()
    }

    get superiorMaps(): SuperiorMap[] {
        return this._superiorMaps
    }

    @Input() update!: (value: CreateSuperiorMap) => void
    @Input() setHasExistingMap?: (value: boolean) => void

    version: number = 0
    orgUnitId: string = ""
    employees = signal<EmployeeMbrad[]>([])
    selectedSuperiorId = signal<string | null>(null)
    employeeOptions = computed<SearchableSelectOption<string>[]>(() =>
        this.employees().map(employee => ({
            value: employee.masterId,
            label: employee.fullName,
            searchText: `${employee.fullName} ${employee.mbrad}`,
        }))
    )
    private superiorSubscription?: Subscription
    private _loadRequestId: number = 0

    constructor(
        private _employeeService: EmployeeService,
        private _messageService: ToastService,
    ) {}

    ngOnDestroy(): void {
        this.superiorSubscription?.unsubscribe()
    }

    private loadSuperiorForEmployee(): void {
        const employeeId = this.employeeId
        const requestId = ++this._loadRequestId

        this.superiorSubscription?.unsubscribe()
        if (!employeeId) {
            this.selectedSuperiorId.set(null)
            return
        }

        this.superiorSubscription = this._employeeService.employees$.pipe(
            switchMap(employees => {
                this.employees.set(employees)
                this.orgUnitId = employees.find(e => e.masterId === employeeId)?.orgUnitId ?? ""
                const currMap = this.superiorMaps.find(m => m.orgUnitId === this.orgUnitId)
                this.setHasExistingMap?.(!!currMap)
                this.version = currMap ? currMap.version : 1
                return currMap
                    ? of(currMap.superiorId).pipe(
                        tap(() => {
                            this.update({
                                orgUnitId: this.orgUnitId,
                                superiorId: currMap.superiorId,
                                version: this.version,
                            })
                        }),
                    )
                    : this._employeeService.GetEmployeeManager(employeeId).pipe(
                        tap(id => {
                            this.update({
                                orgUnitId: this.orgUnitId,
                                superiorId: id,
                                version: this.version,
                            })
                        }),
                    )
            })
        ).subscribe({
            next: (id) => {
                if (requestId !== this._loadRequestId) return
                this.selectedSuperiorId.set(this.employees().some(employee => employee.masterId === id) ? id : null)
            },
            error: err => {
                if (requestId !== this._loadRequestId) return
                this._messageService.add({
                    severity: "error",
                    summary: "Greška",
                    detail: err?.error?.message || "Neuspješno dohvaćanje nadređenog.",
                })
            },
        })
    }

    setSuperior(superiorId: string | null): void {
        this.selectedSuperiorId.set(superiorId)
        if (!superiorId) return

        this.update({
            orgUnitId: this.orgUnitId,
            superiorId,
            version: this.version,
        })
    }
}
